import React, { useState } from 'react';
import { ActivityIndicator, Modal, Platform, Pressable, View } from 'react-native';
import { KeyboardAvoidingView } from 'react-native-keyboard-controller';
import { Ionicons } from '@expo/vector-icons';

import { ThemedText } from '@/components/themed-text';
import { Input } from '@/components/ui/input';
import { GoogleLogo } from '@/components/ui/google-logo';
import { PressableScale } from '@/components/ui/pressable-scale';
import { useAuthStore } from '@/store/authStore';
import { useGoogleOAuth } from '@/hooks/use-google-oauth';
import { useAppleOAuth } from '@/hooks/use-apple-oauth';
import { showToast } from '@/store/toastStore';
import { getApiErrorMessage } from '@/utils/api-error';

interface AddCredentialsModalProps {
  open: boolean;
  /** "Mais tarde" e o X caem aqui: nunca bloqueia o que veio depois. */
  onDismiss: () => void;
  onSaved?: () => void;
}

/**
 * Email e senha para quem entrou só com o WhatsApp.
 *
 * É oferta, não exigência: o telefone já entra sozinho. O email serve para
 * recuperar a conta quando o número muda — que é justamente quando ninguém
 * está com paciência para preencher formulário. Google/Apple abaixo fazem a
 * mesma coisa num toque, e todas as formas passam a valer ao mesmo tempo.
 */
export function AddCredentialsModal({ open, onDismiss, onSaved }: AddCredentialsModalProps) {
  const { setCredentials, linkOAuth } = useAuthStore();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const onLinked = async (token: string) => {
    try {
      await linkOAuth(token);
      setEmail('');
      setPassword('');
      onSaved?.();
      onDismiss();
    } catch (err) {
      setError(getApiErrorMessage(err, 'Não foi possível vincular.'));
    }
  };

  const { signInWithGoogle, isGoogleLoading } = useGoogleOAuth({
    onToken: onLinked,
    onError: () => showToast('Falha ao vincular Google.', 'error'),
  });
  const { signInWithApple, isAppleLoading } = useAppleOAuth({
    onToken: onLinked,
    onError: () => showToast('Falha ao vincular Apple.', 'error'),
  });

  const social = isGoogleLoading || isAppleLoading;
  const disabled = busy || social;

  const submit = async () => {
    if (!email.includes('@')) {
      setError('Informe um email válido.');
      return;
    }
    if (password.length < 8) {
      setError('A senha deve ter no mínimo 8 caracteres.');
      return;
    }
    try {
      setBusy(true);
      setError(null);
      await setCredentials({ email: email.trim(), password });
      setEmail('');
      setPassword('');
      onSaved?.();
      onDismiss();
    } catch (err) {
      setError(getApiErrorMessage(err, 'Não foi possível salvar.'));
    } finally {
      setBusy(false);
    }
  };

  const socialButton =
    'h-12 w-full flex-row items-center justify-center gap-3 rounded-full border border-neutral-200 bg-card active:opacity-70';

  return (
    <Modal visible={open} transparent animationType="slide" onRequestClose={onDismiss}>
      <Pressable className="flex-1 justify-end bg-black/50" onPress={onDismiss}>
        <KeyboardAvoidingView behavior="padding">
          <Pressable
            onPress={(e) => e.stopPropagation()}
            className="rounded-t-3xl bg-card p-6 pb-10"
            style={{ borderCurve: 'continuous' }}
          >
            <View className="mb-4 flex-row items-center justify-between">
              <ThemedText className="font-bold text-xl">Adicionar email e senha</ThemedText>
              <Pressable
                onPress={onDismiss}
                className="h-9 w-9 items-center justify-center rounded-full bg-neutral-100"
                accessibilityLabel="Fechar"
              >
                <Ionicons className="text-neutral-600" name="close" size={18} />
              </Pressable>
            </View>

            <ThemedText className="mb-5 text-sm leading-5 text-neutral-500">
              Para recuperar a conta se você trocar de número. O login por WhatsApp continua
              valendo.
            </ThemedText>

            {!!error && (
              <View className="mb-4 flex-row items-start gap-2 rounded-2xl bg-red-50 p-3">
                <Ionicons className="text-error-600" name="alert-circle" size={17} />
                <ThemedText className="flex-1 text-sm leading-5 text-red-700">{error}</ThemedText>
              </View>
            )}

            <View className="gap-3">
              <Input
                placeholder="seu@email.com"
                value={email}
                onChangeText={setEmail}
                keyboardType="email-address"
                autoCapitalize="none"
                autoComplete="email"
                editable={!disabled}
              />
              <Input
                placeholder="Senha"
                value={password}
                onChangeText={setPassword}
                secureTextEntry
                editable={!disabled}
              />

              <PressableScale
                haptic
                disabled={disabled}
                onPress={submit}
                className="mt-1 w-full flex-row items-center justify-center rounded-full bg-primary py-4"
                style={{ borderCurve: 'continuous' }}
              >
                {busy ? (
                  <ActivityIndicator color="#fff" />
                ) : (
                  <ThemedText className="font-bold text-base text-white">Salvar</ThemedText>
                )}
              </PressableScale>

              <View className="my-1 flex-row items-center">
                <View className="h-px flex-1 bg-neutral-200" />
                <ThemedText className="mx-3 font-medium text-xs text-neutral-400">
                  ou vincule uma conta
                </ThemedText>
                <View className="h-px flex-1 bg-neutral-200" />
              </View>

              <Pressable onPress={signInWithGoogle} disabled={disabled} className={socialButton}>
                {isGoogleLoading ? (
                  <ActivityIndicator size="small" color="#737373" />
                ) : (
                  <>
                    <GoogleLogo size={18} />
                    <ThemedText className="font-semibold text-sm text-neutral-800">
                      Adicionar Google
                    </ThemedText>
                  </>
                )}
              </Pressable>

              {Platform.OS === 'ios' && (
                <Pressable onPress={signInWithApple} disabled={disabled} className={socialButton}>
                  {isAppleLoading ? (
                    <ActivityIndicator size="small" color="#737373" />
                  ) : (
                    <>
                      <Ionicons className="text-neutral-900" name="logo-apple" size={18} />
                      <ThemedText className="font-semibold text-sm text-neutral-800">
                        Adicionar Apple
                      </ThemedText>
                    </>
                  )}
                </Pressable>
              )}

              <Pressable disabled={disabled} onPress={onDismiss} className="items-center py-2">
                <ThemedText className="font-semibold text-sm text-neutral-500">
                  Agora não
                </ThemedText>
              </Pressable>
            </View>
          </Pressable>
        </KeyboardAvoidingView>
      </Pressable>
    </Modal>
  );
}
